import { ComponentProps, ReactNode, useId } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'next-i18next'
import toast from 'react-hot-toast'
import { twMerge } from 'tailwind-merge'
import {
  IMAGE_UPLOAD_SIZE_LIMIT_BYTES,
  IMAGE_UPLOAD_SIZE_LIMIT_MB,
  imageMIMETypes,
  videoMIMETypes,
} from 'shared/constants'
import ClipIcon from 'shared/icons/clip-icon'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { useMultipleFileUpload } from 'modules/attachments/hooks/use-multiple-file-upload'
import useUploadFile from 'modules/attachments/hooks/use-upload-datafile'
import {
  AttachmentTypeEnum,
  AttachmentUploadStatusEnum,
} from 'modules/community/types/post-attachment'

interface UploadAttachmentProps extends ComponentProps<'input'> {
  className?: string
  iconClassName?: string
}

const UploadAttachment = ({
  className,
  iconClassName,
  id: inputId,
  onChange,
  ...props
}: UploadAttachmentProps): ReactNode => {
  const { t } = useTranslation()
  const generatedId = useId()
  const { addError, addAttachment, updateAttachment, limit } = usePostAttachmentsContext()
  const { validateAndGetUploadOptions, uploadFile, activateFile } = useUploadFile()
  const { handleMultipleFileUpload } = useMultipleFileUpload()

  const handleVideoUpload = async (file: File): Promise<void> => {
    const attachmentId = crypto.randomUUID()

    addAttachment({
      id: attachmentId,
      status: AttachmentUploadStatusEnum.Uploading,
      type: AttachmentTypeEnum.Video,
    })

    try {
      await limit(async () => {
        const { id, uploadOptions } = await validateAndGetUploadOptions({
          file,
        })

        await uploadFile({ file, uploadOptions })
        const activatedFile = await activateFile(id)

        updateAttachment(attachmentId, {
          dataFileId: id,
          status: AttachmentUploadStatusEnum.Success,
          url: activatedFile[id].path,
        })
      })
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'Failed to upload video attachment',
        },
      })
      addError(attachmentId, t('post.attachments.upload_failed'))
    }
  }

  const handleImageUpload = async (file: File): Promise<void> => {
    if (file.size > IMAGE_UPLOAD_SIZE_LIMIT_BYTES) {
      toast.error(
        t('post.attachments.file_upload.size_limit_reached', {
          size: `${IMAGE_UPLOAD_SIZE_LIMIT_MB}MB`,
        }),
      )
      return
    }

    const attachmentId = crypto.randomUUID()

    addAttachment({
      id: attachmentId,
      status: AttachmentUploadStatusEnum.Uploading,
      type: AttachmentTypeEnum.Image,
    })

    try {
      await limit(async () => {
        const { id, uploadOptions } = await validateAndGetUploadOptions({
          file,
        })
        await uploadFile({ file, uploadOptions })
        const activatedFile = await activateFile(id)

        updateAttachment(attachmentId, {
          dataFileId: id,
          status: AttachmentUploadStatusEnum.Success,
          url: activatedFile[id].path,
        })
      })
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'Failed to upload image attachment',
        },
      })
      addError(attachmentId, t('post.attachments.upload_failed'))
    }
  }

  const handleSelectFile = async (file: File): Promise<void> => {
    if (
      videoMIMETypes
        .split(',')
        .map(type => type.trim())
        .includes(file.type)
    ) {
      await handleVideoUpload(file)
    } else if (
      imageMIMETypes
        .split(',')
        .map(type => type.trim())
        .includes(file.type)
    ) {
      await handleImageUpload(file)
    } else {
      toast.error(t('post.attachments.file_upload.unsupported_type'))
    }
  }

  const handleSelectFiles = async (files: File[]): Promise<void> => {
    await handleMultipleFileUpload(files, handleSelectFile)
  }

  const handleSelectFileInput = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    onChange?.(e)

    const files = [...(e.target.files ?? [])]

    if (files.length > 0) {
      const validFiles: File[] = []
      const invalidFiles: File[] = []

      for (const file of files) {
        if (
          !imageMIMETypes
            .split(',')
            .map(type => type.trim())
            .includes(file.type) &&
          !videoMIMETypes
            .split(',')
            .map(type => type.trim())
            .includes(file.type)
        ) {
          invalidFiles.push(file)
        } else {
          validFiles.push(file)
        }
      }

      if (invalidFiles.length === 1) {
        toast.error(
          t('post.attachments.file_upload.unsupported_type', {
            fileName: invalidFiles[0].name,
          }),
        )
      } else if (invalidFiles.length > 1) {
        toast.error(
          t('post.attachments.file_upload.unsupported_types_count', {
            count: invalidFiles.length,
          }),
        )
      }

      if (validFiles.length > 0) {
        await handleSelectFiles(validFiles)
      }
    }

    // NOTE: Reset value to have the ability to open the same file https://stackoverflow.com/questions/4109276/how-to-detect-input-type-file-change-for-the-same-file
    e.target.value = ''
  }

  const id = inputId ?? generatedId

  return (
    <label htmlFor={id} className={twMerge('cursor-pointer font-medium', className)}>
      <input
        {...props}
        type="file"
        className="invisible absolute h-1 w-0"
        onChange={handleSelectFileInput}
        accept="image/jpeg, image/png, image/webp, video/x-msvideo, video/mp4, video/mpeg, video/ogg, video/webm"
        multiple
        id={id}
      />
      <span className="sr-only">{t('global.upload_image')}</span>
      <ClipIcon className={twMerge('size-6 text-text-primary', iconClassName)} />
    </label>
  )
}

export { UploadAttachment }
